import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useCallback, useEffect } from 'react'
import { create } from 'zustand'
import type { DeeplinkPreset } from '../interfaces/deeplinkCatalog'
import {
  deleteCustomDeeplink,
  fetchCommunityDeeplinks,
  fetchUserCustomDeeplinks,
  getLocalCustomDeeplinks,
  insertCustomDeeplink,
  mergeCustomDeeplinks,
  updateCustomDeeplink,
} from './deeplinkCatalogStorage'

interface DeeplinkCatalogState {
  customPresets: DeeplinkPreset[]
  communityPresets: DeeplinkPreset[]
  loading: boolean
  error: string | null
  setCustomPresets: (
    updater: DeeplinkPreset[] | ((prev: DeeplinkPreset[]) => DeeplinkPreset[]),
  ) => void
  setCommunityPresets: (
    updater: DeeplinkPreset[] | ((prev: DeeplinkPreset[]) => DeeplinkPreset[]),
  ) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useDeeplinkCatalogStore = create<DeeplinkCatalogState>((set) => ({
  customPresets: getLocalCustomDeeplinks(),
  communityPresets: [],
  loading: false,
  error: null,
  setCustomPresets: (updater) =>
    set((state) => ({
      customPresets:
        typeof updater === 'function' ? updater(state.customPresets) : updater,
    })),
  setCommunityPresets: (updater) =>
    set((state) => ({
      communityPresets:
        typeof updater === 'function'
          ? updater(state.communityPresets)
          : updater,
    })),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
}))

export interface UseDeeplinkCatalogReturn {
  customPresets: DeeplinkPreset[]
  communityPresets: DeeplinkPreset[]
  loading: boolean
  error: string | null
  addCustomPreset: (
    item: Omit<DeeplinkPreset, 'id' | 'created_at' | 'updated_at'>,
  ) => Promise<DeeplinkPreset>
  editCustomPreset: (item: DeeplinkPreset) => Promise<DeeplinkPreset>
  removeCustomPreset: (id: string) => Promise<boolean>
  refreshCatalog: () => Promise<void>
}

export function useDeeplinkCatalog(): UseDeeplinkCatalogReturn {
  const user = useAuthStore((state) => state.user)
  const customPresets = useDeeplinkCatalogStore((s) => s.customPresets)
  const communityPresets = useDeeplinkCatalogStore((s) => s.communityPresets)
  const loading = useDeeplinkCatalogStore((s) => s.loading)
  const error = useDeeplinkCatalogStore((s) => s.error)
  const setCustomPresets = useDeeplinkCatalogStore((s) => s.setCustomPresets)
  const setCommunityPresets = useDeeplinkCatalogStore(
    (s) => s.setCommunityPresets,
  )
  const setLoading = useDeeplinkCatalogStore((s) => s.setLoading)
  const setError = useDeeplinkCatalogStore((s) => s.setError)

  const refreshCatalog = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)

    try {
      // 1. Fetch community public presets
      const community = await fetchCommunityDeeplinks(supabase)
      setCommunityPresets(community)

      // 2. Fetch user custom presets from Supabase if logged in
      if (user?.id) {
        const remoteUserPresets = await fetchUserCustomDeeplinks(
          supabase,
          user.id,
        )
        const local = getLocalCustomDeeplinks()
        const merged = mergeCustomDeeplinks(local, remoteUserPresets)
        setCustomPresets(merged)
      } else {
        setCustomPresets(getLocalCustomDeeplinks())
      }
    } catch (err) {
      console.warn('Failed to refresh deeplink catalog:', err)
      setError('Unable to load latest community presets.')
    } finally {
      setLoading(false)
    }
  }, [user?.id, setCommunityPresets, setCustomPresets, setError, setLoading])

  useEffect(() => {
    refreshCatalog()
  }, [refreshCatalog])

  const addCustomPreset = useCallback(
    async (
      item: Omit<DeeplinkPreset, 'id' | 'created_at' | 'updated_at'>,
    ): Promise<DeeplinkPreset> => {
      const created = await insertCustomDeeplink(supabase, user?.id, item)
      setCustomPresets((prev) => [
        created,
        ...prev.filter((p) => p.id !== created.id),
      ])
      if (created.is_public) {
        setCommunityPresets((prev) => [
          created,
          ...prev.filter((p) => p.id !== created.id),
        ])
      }
      return created
    },
    [user?.id, setCustomPresets, setCommunityPresets],
  )

  const editCustomPreset = useCallback(
    async (item: DeeplinkPreset): Promise<DeeplinkPreset> => {
      const updated = await updateCustomDeeplink(supabase, user?.id, item)
      setCustomPresets((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p)),
      )
      if (updated.is_public) {
        setCommunityPresets((prev) => {
          const exists = prev.some((p) => p.id === updated.id)
          return exists
            ? prev.map((p) => (p.id === updated.id ? updated : p))
            : [updated, ...prev]
        })
      } else {
        setCommunityPresets((prev) => prev.filter((p) => p.id !== updated.id))
      }
      return updated
    },
    [user?.id, setCustomPresets, setCommunityPresets],
  )

  const removeCustomPreset = useCallback(
    async (id: string): Promise<boolean> => {
      const success = await deleteCustomDeeplink(supabase, user?.id, id)
      if (success) {
        setCustomPresets((prev) => prev.filter((p) => p.id !== id))
        setCommunityPresets((prev) => prev.filter((p) => p.id !== id))
      }
      return success
    },
    [user?.id, setCustomPresets, setCommunityPresets],
  )

  return {
    customPresets,
    communityPresets,
    loading,
    error,
    addCustomPreset,
    editCustomPreset,
    removeCustomPreset,
    refreshCatalog,
  }
}
